import { z } from 'zod';

/**
 * Shared primitives for SiliconX DTOs.
 *
 * Brands keep ergonomic primitive types (string, number) while making
 * misuse a compile error: an asset_id (uuid) and a snapshot_id (uuid) are
 * both strings but should never be interchangeable.
 *
 * Time is always ISO-8601 strings over the wire (pg `timestamptz` →
 * `to_jsonb` already emits ISO). Numbers are JSON numbers; we widen
 * monetary fields to `number` for ergonomic display and rely on the
 * server-side numeric(12,2) precision for storage truth.
 */

export const uuidSchema = z.string().uuid().brand('Uuid');
export type Uuid = z.infer<typeof uuidSchema>;

export const isoTimestampSchema = z
  .string()
  .datetime({ offset: true })
  .brand('IsoTimestamp');
export type IsoTimestamp = z.infer<typeof isoTimestampSchema>;

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .brand('IsoDate');
export type IsoDate = z.infer<typeof isoDateSchema>;

/**
 * USD amount, accepted as a number OR a numeric-typeable string (pg
 * `numeric` serializes as string in node-postgres by default). Server-side
 * adapters coerce to number before responding; this schema accepts both
 * for paranoia.
 */
export const usdAmountSchema = z.coerce.number().brand('Usd');
export type Usd = z.infer<typeof usdAmountSchema>;

export const pageCursorSchema = z.string().min(1).brand('PageCursor');
export type PageCursor = z.infer<typeof pageCursorSchema>;

export const pageMetaSchema = z.object({
  nextCursor: pageCursorSchema.nullable(),
  limit: z.number().int().positive(),
});
export type PageMeta = z.infer<typeof pageMetaSchema>;

/**
 * Standard error envelope returned by every API endpoint on failure.
 * Matches the error middleware shape in apps/api/src/middleware/error.ts.
 */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
